import { createRouter, createWebHistory } from 'vue-router';

import { toBase32 } from '@/utils/toBase32.js';
import store from '@/store';
import { saveScrollPosition, restoreScrollPosition } from '@/utils/scrollControl';

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      name: 'home',
      meta: { layout: 'default', auth: true },
      beforeEnter: (to, from, next) => {
        const isAuthenticated = store.getters.getAuthStatus;
        if (!isAuthenticated) {
          next({ name: 'welcome', replace: true });
          return;
        }
        next();
      },
      component: () => import('../views/index.vue'),
    },
    // {
    //   path: '/welcome',
    //   name: 'welcome',
    //   meta: { layout: 'guest', auth: false },
    //   beforeEnter: (to, from, next) => {
    //     const isAuthenticated = store.getters.getAuthStatus;
    //     if (isAuthenticated) {
    //       next({ name: 'home', replace: true });
    //       return;
    //     }
    //     next();
    //   },
    //   component: () => import('../views/LandingPage.vue'),
    // },
    {
      path: '/search',
      name: 'search',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/search.vue'),
    },
    {
      path: '/modern-feed',
      name: 'modern-feed',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/ModernFeedView.vue'),
    },
    {
      path: '/sign-in-with-x',
      name: 'sign-in-with-x',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/sign-in-with-x.vue'),
    },
    {
      path: '/oauth-callback-bluesky',
      name: 'oauth-callback-bluesky',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/oauth-callback-bluesky.vue'),
    },
    {
      path: '/declaration',
      name: 'declarationPage',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/declarationPage.vue'),
    },
    {
      path: '/about',
      name: 'aboutPage',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/aboutPage.vue'),
    },
    {
      path: '/privacy',
      name: 'privacy',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/privacy.vue'),
    },
    {
      path: '/term-of-service',
      name: 'terms',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/termsOfService.vue'),
    },
    {
      path: '/post/:id',
      name: 'post',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/post.vue'),
    },
    {
      path: '/reply/:id',
      name: 'reply',
      meta: { layout: 'default', auth: true, isReply: true },
      component: () => import('../views/post.vue'),
    },
    {
      path: '/sign-in',
      name: 'sign-in',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/sign-in.vue'),
    },
    {
      path: '/profile/:id',
      name: 'profile',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/profile.vue'),
    },
    {
      path: '/user/:username',
      name: 'username',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/profile.vue'),
    },
    {
      path: '/group/:id',
      name: 'org',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/org.vue'),
    },
    {
      path: '/groups',
      name: 'groups',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/groups.vue'),
    },
    {
      path: '/groups/:userId',
      name: 'user-groups',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/groups.vue'),
    },
    // Dedicated help/profile route for the built-in help account so left/right bars
    // and layout match other pages. This must be declared before the generic
    // /user/:username route to take precedence.
    {
      path: '/user/sezushelp',
      name: 'sezushelp',
      meta: { layout: 'default', auth: false },
      component: () => import('../views/sezushelp.vue'),
    },
    {
      path: '/group/:groupId/channel/:id',
      name: 'channel',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/channel.vue'),
    },
    {
      path: '/followers/:id',
      name: 'followers',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/followers.vue'),
    },
    {
      path: '/following/:id',
      name: 'following',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/followers.vue'),
    },
    {
      path: '/notifications',
      name: 'notifications',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/notifications.vue'),
    },
    {
      path: '/twoFactorAuth',
      name: 'twoFactorAuth',
      meta: { layout: 'default', auth: true },
      component: () => import('../views/twoFactorAuth.vue'),
    },
    {
      path: '/sign-up',
      name: 'signup',
      meta: { layout: 'guest', auth: false},
      component: () => import('../views/onboarding/SignUp.vue')
    },
    // {
    //   path: '/onboarding/create-username',
    //   name: 'create-username',
    //   meta: { layout: 'guest', auth: false },
    //   component: () => import('../views/onboarding/CreateUsername.vue'),
    // },
    // {
    //   path: '/onboarding/create-account',
    //   name: 'create-account',
    //   meta: { layout: 'guest', auth: false },
    //   component: () => import('../views/onboarding/CreateAccount.vue'),
    // },
    {
      path: '/onboarding/verification-code',
      name: 'verification-code',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/onboarding/VerificationCode.vue'),
    },
    {
      path: '/onboarding/create-password',
      name: 'create-password',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/onboarding/CreatePassword.vue'),
    },
    {
      path: '/onboarding/add-avatar',
      name: 'add-avatar',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/onboarding/AddAvatar.vue'),
    },
    {
      path: '/onboarding/login-method',
      name: 'login-method',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/onboarding/LoginMethod.vue'),
    },
    {
      path: '/siwf/callback',
      name: 'siwf-callback',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/siwf-callback.vue'),
    },
    {
      path: '/siwf-login',
      name: 'siwf-login',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/siwf-login.vue'),
    },
    {
      path: '/siwf/register',
      name: 'siwf-register',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/siwf-register.vue'),
    },
    {
      // Legacy alias to support old links like /siwf/register?...
      path: '/siwf/register',
      redirect: { name: 'siwf-register' },
      meta: { layout: 'guest', auth: false },
    },
    {
      path: '/verify-email',
      name: 'verify-email',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/verify-email.vue'),
    },
    {
      // path: '/check-visit-links',
      path: '/l/:id',
      name: 'check-visit-links',
      meta: { layout: 'guest', auth: false },
      component: () => import('../views/check-visit-links.vue'),
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      meta: { layout: 'guest', auth: false },
      component: () => import('../components/Page404/index.vue'),
    },
  ],
});

// needed later, implement it per-request (e.g. on fetch URLs) instead of mutating routes.

let previousRoute = null;
router.beforeEach((to, from, next) => {
  // Start full-page loader on route navigation start
  try { store.commit('setRouteLoading', true); } catch {}
  // Save scroll position when leaving the home page or profile
  if (from.name === 'home' || from.name === 'profile' || from.name === 'username') {
    saveScrollPosition(from.name);
  }

  // Check authentication for routes that require it
  const isAuthenticated = store.getters.getAuthStatus;

  // Preserve original navigation behavior without added redirects for welcome/home

  // If user is not authenticated and trying to access protected routes, redirect to landing
  if (!isAuthenticated && to.meta.auth) {
    // next('/welcome');
    next('/sign-in');
    return;
  }

  next();
  previousRoute = from;
});

router.afterEach((to, from) => {
  // Restore scroll position when returning to the home page or profile
  if (
    (to.name === 'home' && from.name === 'post') ||
    to.name === 'username' ||
    (to.name === 'profile' && (from.name === 'post' || from.name === 'reply'))
  ) {
    // Small delay for the component to fully load
    restoreScrollPosition(to.name, 300);
  }

  // Only send tracking if the path actually changed
  if (to.path !== from.path) {
    store.dispatch('sendTrackPage', { url: to.path });
  }

  // End full-page loader on route navigation complete
  try { store.commit('setRouteLoading', false); } catch {}
});

// Ensure loader hides on navigation errors (e.g., dynamic import failures)
router.onError(() => {
  try { store.commit('setRouteLoading', false); } catch {}
});

export function goBack() {
  const current = router.currentRoute.value;
  if (current.name === 'home') return;

  // Try native back first
  if (window.history.length > 1) {
    router.back();
    return;
  }

  // Fallback to previousRoute if available
  if (previousRoute && previousRoute.name && previousRoute.name !== current.name) {
    router.push({ name: previousRoute.name, params: previousRoute.params, query: previousRoute.query });
    return;
  }

  // Final fallback
  router.push({ name: 'home' });
}

export { previousRoute };
export default router;
